import { Moment } from 'moment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { toCentavos, fromCentavos } from '../shared/utils/money.util';
import { ICoin } from '../shared/models/coin.model';
import { ICostCenter } from '../shared/models/cost-center.model';
import { IFluxo } from '../shared/models/fluxo.model';
import { IStrech } from '../shared/models/strech.model';
import { AlertService } from './alert.service';
import { BaseService } from './base/base.service';
import { UtilService } from './util.service';
import { IRequestRid } from '../shared/models/request-rid.model';
import { UF } from '../shared/models/UF.model';
import { City } from '../shared/models/city.model';
import { AgenteBloqueio } from '../shared/models/agente-bloqueio.model';
import { ValorReferenciaDTO } from '../shared/models/valor-referencia.model';
import { HistoricoAprovacaoConsultaDTO } from '../shared/models/historico-aprovacao.model';
import { CalculoTotalTrechoDTO, TotalTrechoConvertidoDTO } from '../shared/models/total-trecho-convertido.model';

@Injectable({
  providedIn: 'root'
})

export class RequestService extends BaseService<any> {

  serviceName: string = "solicitacao";

  public form = new FormGroup({
    id: this.fb.control(null),
    numero: this.fb.control(null),
    trechos: new FormArray([]),
    rids: new FormArray([]),
    ridFornecedor: new FormArray([]),
    notaTecnica: this.fb.control(null),
    ridTelefoniaDTO: new FormGroup({}),
    viagemEmNomeDe: this.fb.control(null),
    nomeAgenteViagemEmNomeDe: this.fb.control(null),
    solicitadoPor: this.fb.control(null),
    dataSolicitacao: this.fb.control(null),
    fluxo: this.fb.control(1, Validators.required),
    totalSolicitacao: this.fb.control(0, [Validators.required, Validators.min(1)]),
    prestacaoContas: this.fb.control(false),
    solicitacaoReembolso: this.fb.control(false),
    valorAPagar: this.fb.control(0),
    valorAReceber: this.fb.control(0),
    totalPrestacaoContas: this.fb.control(0),
    unidade: this.fb.control(null),
    escritorio: this.fb.control(null),
    status: this.fb.control(null),
    totalDiarias: this.fb.control(0),
    totalRealizado: this.fb.control(0),
    totalTrechoReal: this.fb.control(0),
    totalRid: this.fb.control(0),
    cotacao: this.fb.control(0),
    idMoeda: this.fb.control(null),
    totalSolicitacaoSemLocalizadorWts: this.fb.control(0),
    totalSolicitacaoSemPassagem: this.fb.control(0),
    justificativa: this.fb.control(null),
    motivoAlteracao: this.fb.control(null),
    convidado: this.fb.control(false),
    totalInternacionalDiaria: this.fb.control(0),
    totalInternacionalPassagem: this.fb.control(0),
  }, { validators: [this.isStrechValidDates] });
  public error = false;
  public countriesEmitter$ = new Subject<IFluxo>();
  public strechEmitter$ = new Subject<any>();
  public fluxos = [];

  constructor(
    private fb: FormBuilder,
    protected http: HttpClient,
    protected utilService: UtilService,
    private alertService: AlertService,
  ) {
    super(http, utilService);

    const strechObj = this.getStrechObj();
    const strechFormGroup = this.newStrech(strechObj);
    const strechs = this.form.controls.trechos as FormArray;

    strechs.push(strechFormGroup);
  }

  prepareSolicitacaoForApi(model: any): any {
    const payload = { ...model };
    payload.valorAPagar = toCentavos(payload.valorAPagar);
    payload.valorAReceber = toCentavos(payload.valorAReceber);
    payload.totalPrestacaoContas = toCentavos(payload.totalPrestacaoContas);
    return payload;
  }

  normalizeSolicitacaoFromApi(res: any): any {
    if (!res) return res;
    return {
      ...res,
      valorAPagar: fromCentavos(res.valorAPagar),
      valorAReceber: fromCentavos(res.valorAReceber),
      totalPrestacaoContas: fromCentavos(res.totalPrestacaoContas),
    };
  }

  sendRequest(request: IRequestRid) {
    const payload = this.prepareSolicitacaoForApi(request);
    return this.http.post<IRequestRid>(this.urlApi + this.serviceName, payload);
  }

  calcularTotalConvertido(dto: CalculoTotalTrechoDTO) {
    return this.http.post<TotalTrechoConvertidoDTO>(
      this.urlApi + 'trecho/calcular-total-convertido', dto
    );
  }

  getById(id: number, modelName?: string, suffix?: string) {
    return super.getById(id, modelName, suffix).pipe(
      map(res => this.normalizeSolicitacaoFromApi(res))
    );
  }

  edit(model: any) {
    const payload = this.prepareSolicitacaoForApi(model);
    return this.http.put(this.urlApi + this.serviceName, payload);
  }

  cadastroDeFornecedores(dados){
    return this.http.post<any>(this.urlApi +  'envio-email/fornecedor/solicitacao', dados);
  }

  getRequests(filter, phoneBills?) {
    return this.http.post<any>(this.urlApi + this.serviceName + (phoneBills ? '/buscar-telefonia' : '/buscar'), filter);
  }

  getRequestingAreas() {
    return this.http.get<any>(this.urlApi + 'area-solicitante');
  }

  getUFs() {
    return this.http.get<UF[]>(this.urlApi + 'uf');
  }

  getCities(model) {
    return this.http.post<City[]>(this.urlApi + 'cidade', model);
  }

  getCountriesAddress() {
    return this.http.get<any[]>(this.urlApi + 'pais');
  }

  getFiltersQnt(model) {
    return this.http.post<any>(this.urlApi + this.serviceName + '/buscar-total-status', model);
  }

  updateStatus(model) {
    return this.http.post(this.urlApi + this.serviceName + '/alterar-status', model);
  }

  savePrice(model) {
    return this.http.post(this.urlApi + this.serviceName + '/salvar-cotacao', model);
  }

  findDuplicateOrSameDestinationTrips(model) {
    return this.http.post<any>(this.urlApi + this.serviceName + '/buscar-viagem-sequencial', model);
  }

  emitCountries(res: IFluxo) {
    this.countriesEmitter$.next(res);
  }

  getCountries() {
    return this.countriesEmitter$.asObservable();
  }

  getCoins() {
    return this.http.get<ICoin[]>(this.urlApi + 'moeda' + '/buscar');
  }

  getMotivosPrestacao() {
    return this.http.get(this.urlApi + 'motivosprestacao' + '/buscar');
  }

  getCostCenter() {
    return this.http.get<ICostCenter[]>(this.urlApi + 'centro-custo' + '/buscar');
  }

  uploadFiles(idStrech: number, files: FormData) {
    let headers = new HttpHeaders();

    headers = headers.append('no-content-type', 'no-content-type');

    return this.http.post(this.urlApi + this.serviceName + '/salvar-anexo/' + idStrech, files, { headers, responseType: 'text' });
  }

  uploadFilesNotaTecnica(idCodAgente: number, files: FormData) {
    let headers = new HttpHeaders();

    headers = headers.append('no-content-type', 'no-content-type');

    return this.http.post(this.urlApi + this.serviceName + '/salvar-anexo-nota-tecnica/' + idCodAgente, files, { headers, responseType: 'text' });
  }

  uploadFilesReceipts(idStrech: number, files: FormData) {
    let headers = new HttpHeaders();

    headers = headers.append('no-content-type', 'no-content-type');

    return this.http.post(this.urlApi + this.serviceName + '/salvar-anexo-passagem/' + idStrech, files, { headers, responseType: 'text' });
  }

  deleteFile(idFile: number) {
    return this.http.post(this.urlApi + this.serviceName + '/deletar-anexo?id=' + idFile, {});
  }

  downloadFile(id: number) {
    return this.http.get<Blob>(this.urlApi + this.serviceName + '/download/' + id, { observe: 'response', responseType: 'blob' as 'json' });
  }

  emitStrech() {
    this.strechEmitter$.next(null);
  }

  getStrech() {
    return this.strechEmitter$.asObservable();
  }

  newStrech(trecho?: IStrech, isRid?: boolean, isRidFornecedor?: boolean): FormGroup {
    if (!trecho)
      trecho = this.getStrechObj();

    return this.fb.group({
      id: [trecho.id ? trecho.id : null],
      idComprometido: [trecho.idComprometido ? trecho.idComprometido : null],
      origemLocalizadorWts: [trecho.origemLocalizadorWts],
      origemRepetirLocalizadorWts: [trecho.origemRepetirLocalizadorWts],
      origemValorLocalizadorWts: [trecho.origemValorLocalizadorWts],
      origemPais: [trecho.origemPais, Validators.required],
      origemEstado: [trecho.origemEstado],
      origemCidade: [trecho.origemCidade],
      origemCidadeNome: [trecho.origemCidadeNome ? trecho.origemCidadeNome : null],
      origemEstadoNome: [trecho.origemEstadoNome ? trecho.origemEstadoNome : null],
      origemTipoDeLocomocao: [trecho.origemTipoDeLocomocao, Validators.required],
      origemData: [trecho.origemData, [Validators.required]],
      origemHora: [trecho.origemHora, [Validators.required, this.utilService.isValidHourForm]],
      dataHoraOrigem: [trecho.dataHoraOrigem ? trecho.dataHoraOrigem : null],
      dataRetornoReal: [trecho.dataRetornoReal ? trecho.dataRetornoReal : null],
      diaSaidaDataReal: [trecho.diaSaidaDataReal ? trecho.diaSaidaDataReal : null],
      horaSaidaDataReal: [trecho.horaSaidaDataReal ? trecho.horaSaidaDataReal : null],
      diaRetornoDataReal: [trecho.diaRetornoDataReal ? trecho.diaRetornoDataReal : null],
      horaRetornoDataReal: [trecho.horaRetornoDataReal ? trecho.horaRetornoDataReal : null],
      dataSaidaReal: [trecho.dataSaidaReal ? trecho.dataSaidaReal : null],
      destinoLocalizadorWts: [trecho.destinoLocalizadorWts],
      destinoRepetirLocalizadorWts: [trecho.destinoRepetirLocalizadorWts],
      destinoValorLocalizadorWts: [trecho.destinoValorLocalizadorWts],
      destinoPais: [trecho.destinoPais, Validators.required],
      destinoEstado: [trecho.destinoEstado],
      destinoCidade: [trecho.destinoCidade],
      destinoCidadeNome: [trecho.destinoCidadeNome ? trecho.destinoCidadeNome : null],
      destinoEstadoNome: [trecho.destinoEstadoNome ? trecho.destinoEstadoNome : null],
      destinoTipoDeLocomocao: [trecho.destinoTipoDeLocomocao, Validators.required],
      destinoData: [trecho.destinoData, [Validators.required]],
      destinoHora: [trecho.destinoHora, [Validators.required, this.utilService.isValidHourForm]],
      dataHoraDestino: [trecho.dataHoraDestino ? trecho.dataHoraDestino : null],
      acao: [trecho.acao, Validators.required],
      propostaDiaria: [trecho.propostaDiaria, Validators.required],
      motivo: [trecho.motivo, [Validators.required, Validators.maxLength(2000)]],
      areaSolicitante: [trecho.areaSolicitante, Validators.required],
      centroDeCusto: [trecho.centroDeCusto != null ? String(trecho.centroDeCusto) : null, Validators.required],
      valorDiaria: [{ value: trecho.valorDiaria, disabled: true }],
      totalDiariaReal: [trecho.totalDiariaReal || trecho.totalDiariaTrechoReal],
      outroValorDiaria: [trecho.outroValorDiaria],
      qtdDiaria: [{ value: trecho.qtdDiaria, disabled: true }],
      qtdDiariaReal: [trecho.qtdDiariaReal],
      naoPercorrido: [trecho.naoPercorrido ? trecho.naoPercorrido : false],
      totalDiaria: [{ value: trecho.totalDiaria ?? trecho.totalDiariaTrecho, disabled: true }],
      totalTrecho: [trecho.totalTrecho],
      valorAPagar: [trecho.valorAPagar],
      valorAReceber: [trecho.valorAReceber],
      totalTrechoReal: [trecho.totalTrechoReal],
      observacoes: [trecho.observacoes],
      relatorioDirex: [trecho.relatorioDirex],
      comprovanteDirex: [trecho.comprovanteDirex],
      relatoriosTrecho: [trecho.relatoriosTrecho ? trecho.relatoriosTrecho : null],
      destinoValorPassagem: [trecho.destinoValorPassagem],
      origemValorPassagem: [trecho.origemValorPassagem],
      destinoRepetirValorPassagem: [trecho.destinoRepetirValorPassagem],
      origemRepetirValorPassagem: [trecho.origemRepetirValorPassagem],
      comprovantesTrecho: [trecho.comprovantesTrecho ? trecho.comprovantesTrecho : null],
      dataOrigemEmissaoWts: [trecho.dataOrigemEmissaoWts],
      dataOrigemCompraWts: [trecho.dataOrigemCompraWts],
      dataDestinoEmissaoWts: [trecho.dataDestinoEmissaoWts],
      dataDestinoCompraWts: [trecho.dataDestinoCompraWts]
    }, {
      validator: this.isValidDates
    });
  }


  getStrechObj(trecho?: IStrech): IStrech {
    return {
      id: null,
      idComprometido: null,
      origemLocalizadorWts: null,
      origemRepetirLocalizadorWts: false,
      origemValorLocalizadorWts: 0,
      origemPais: trecho?.destinoPais ?? this.getBrazilInitials(),
      origemEstado: trecho?.destinoEstado ?? null,
      origemCidade: trecho?.destinoCidade ?? null,
      origemCidadeNome: trecho?.destinoCidadeNome ?? '',
      origemEstadoNome: trecho?.destinoEstadoNome ?? '',
      origemTipoDeLocomocao: trecho?.destinoTipoDeLocomocao ?? null,
      origemData: trecho?.destinoData ?? null,
      origemHora: null,
      dataRetornoReal: null,
      diaSaidaDataReal: null,
      horaSaidaDataReal: null,
      diaRetornoDataReal: null,
      dataSaidaReal: '',
      destinoLocalizadorWts: null,
      destinoRepetirLocalizadorWts: false,
      destinoValorLocalizadorWts: 0,
      destinoPais: this.getBrazilInitials(),
      destinoEstado: null,
      destinoCidade: null,
      destinoCidadeNome: '',
      destinoEstadoNome: '',
      destinoTipoDeLocomocao: null,
      destinoData: null,
      destinoHora: null,
      acao: null,
      propostaDiaria: null,
      motivo: null,
      areaSolicitante: null,
      centroDeCusto: null,
      valorDiaria: 0,
      totalDiariaReal: 0,
      qtdDiaria: 0,
      qtdDiariaReal: 0,
      totalDiaria: 0,
      totalDiariaTrecho: 0,
      totalDiariaTrechoReal: 0,
      dataHoraDestino: '',
      dataHoraOrigem: '',
      naoPercorrido: false,
      totalTrecho: 0,
      valorAPagar: 0,
      totalTrechoReal: 0,
      observacoes: null,
      relatorioDirex: null,
      comprovanteDirex: null,
      destinoValorPassagem: null,
      origemValorPassagem: null,
      destinoRepetirValorPassagem: false,
      origemRepetirValorPassagem: false,
    }
  }

  getBrazilInitials() {
    return 'BR';
  }

  getCoinReal(coins) {
    return coins.find(coin => coin.nomeMoeda == 'Real').id;
  }

  getCoinDollar(coins) {
    return coins.find(coin => coin.nomeMoeda == 'Dólar').id;
  }

  isFluxoParaguaiArgentina(fluxoName: string): boolean {
    return fluxoName == 'Paraguai/Argentina';
  }

  removeLastStrech() {
    const strechs = this.form.get('trechos') as FormArray;

    if (strechs.length > 1) {
      strechs.removeAt(strechs.length - 1);
      this.alertService.success('trecho removido com sucesso!');

      this.calcTotal();
    }
  }

  resetForm(total?, valorAPagar?, valorAReceber?, totalPrestacaoContas?, prestacaoContas?, solicitacaoReembolso?, convidado?) {
    this.form.patchValue({
      id: null,
      numero: null,
      viagemEmNomeDe: null,
      fluxo: 1,
      totalSolicitacao: total ? total : 0,
      valorAPagar: valorAPagar ? valorAPagar : 0,
      valorAReceber: valorAReceber ? valorAReceber : 0,
      totalPrestacaoContas: totalPrestacaoContas ? totalPrestacaoContas : 0,
      nomeAgenteViagemEmNomeDe: null,
      unidade: null,
      escritorio: null,
      solicitadoPor: null,
      dataSolicitacao: null,
      status: null,
      prestacaoContas: prestacaoContas ? prestacaoContas : false,
      solicitacaoReembolso: solicitacaoReembolso ? solicitacaoReembolso : false,
      totalDiarias: null,
      totalRealizado: null,
      totalRid: null,
      cotacao: null,
      idMoeda: null,
      totalSolicitacaoSemLocalizadorWts: null,
      justificativa: null,
      motivoAlteracao: null,
      convidado: convidado ? convidado : false,
      totalInternacionalDiaria: null,
      totalInternacionalPassagem: null,
    });

    this.form.controls.viagemEmNomeDe.removeValidators(Validators.required);

    this.resetStrechs();
    this.resetRids();
    this.resetRidFornecedor();
    this.resetPhoneBills();
  }

  resetStrechs() {
    const strechs = this.form.controls.trechos as FormArray;

    strechs.clear();
  }

  resetRids(calcTotal?: boolean) {
    const rids = this.form.controls.rids as FormArray;

    rids.clear();

    if (calcTotal)
      this.calcTotal();
  }

  resetRidFornecedor() {
    const ridsFornecedor = this.form.controls.ridFornecedor as FormArray;

    ridsFornecedor.clear();
  }

  resetPhoneBills() {
    const phoneBills = this.form.controls.ridTelefoniaDTO as FormGroup;

    if (phoneBills)
      phoneBills.reset();
  }

  handlePrice() {
    const form = this.form;
    const priceValue: string | number = form.getRawValue().cotacao;

    if (typeof priceValue === 'string') {
        const trimmedValue = (priceValue as string).trim();

        if (trimmedValue !== '') {
            const priceValueConverted = Number(trimmedValue.replace(',', '.'));

            if (!isNaN(priceValueConverted)) {
                return priceValueConverted;
            }
        }
    } else if (typeof priceValue === 'number') {
        return priceValue;
    }

    return null; 
  }

  calcTotal(telaDetalhes?: boolean) {
    let total = 0;
    let totalAPagar = 0;
    let totalAReceber = 0;
    let totalTrechoReal = 0;
    let totalBalance = 0;
    let totalAdiantado = 0;
    let totalRid = 0;
    let totalAdvance = 0;
    let totalRealized = 0;

    let totalInternacionalDiaria = 0;
    let totalInternacionalPassagem = 0;
    const strechs = this.form.get('trechos') as FormArray;
    const rids = this.form.get('rids') as FormArray;
    const ridsFornecedor = this.form.get('ridFornecedor') as FormArray;
    const res: IStrech[] = strechs.getRawValue();
    const phoneBills = this.form.get('ridTelefoniaDTO') as FormGroup;
    const formValue = this.form.getRawValue();
    const price = (this.handlePrice() != null && this.handlePrice() > 0) ? this.handlePrice() : 1;
    const isPrestacaoContaIntegral = this.form.get('prestacaoContas').value;
    if (res) {
      for (let i = 0; i < res.length; i++) {
        const strech = res[i];
        const strechForm = strechs.controls[i] as FormGroup;

        totalRealized += strech.totalDiariaReal;
        totalTrechoReal = strech.totalDiariaReal;
        if (strech.totalTrecho) {
          if (typeof strech.totalTrecho == 'string') {
            totalAdvance += Number((strech.totalTrecho as string).replace(',', '.'));
            total += Number((strech.totalTrecho as string).replace(',', '.'));
          }

          if (typeof strech.totalTrecho == 'number') {
            totalAdvance += strech.totalTrecho;
            total += strech.totalTrecho;
          }
        }
          
        let atribuiTotalAReceber = strech.naoPercorrido && strech.totalDiaria && !strech.valorAReceber;
        if (typeof strech.totalDiaria == 'string') {
            if (atribuiTotalAReceber) {
                totalAReceber += Number(strech.totalDiaria.replace(',', '.'));
            }
            else if (strech.totalTrechoReal && !strech.valorAReceber && (strech.totalTrechoReal < Number(strech.totalDiaria.replace(',', '.'))) ) {
                totalAReceber += Number(strech.totalDiaria.replace(',', '.')) - strech.totalTrechoReal;
            }
          totalAdiantado += Number(strech.totalDiaria.replace(',', '.'));
        }

        if (typeof strech.totalDiaria == 'number') {
          if(atribuiTotalAReceber) { 
            totalAReceber += strech.totalDiaria;
          } else if (strech.totalTrechoReal) {
              totalAReceber += strech.totalDiaria - strech.totalTrechoReal;
          }
          totalAdiantado += strech.totalDiaria;
        }
        
        if (strech.valorAReceber) {
          totalAReceber += strech.valorAReceber;
        }

        if (strech.valorAPagar && !isPrestacaoContaIntegral) {
          totalAPagar += strech.valorAPagar;
        }

        if (totalTrechoReal != formValue.totalTrechoReal)
          strechForm.patchValue({ totalTrechoReal: totalTrechoReal }, { emitEvent: false });

        if (formValue.fluxo == 2 || formValue.fluxo == 5) { //Internacional ou Paraguai/Argentina
          if (typeof strech.totalDiaria == 'string')
            totalInternacionalDiaria += Number((strech.totalDiaria as string).replace(',', '.'));

         // Verifica se o valor origem não foi repetido anteriormente
          if (strech.origemValorLocalizadorWts && (!res[i - 1]?.origemRepetirLocalizadorWts || !res[i - 1]?.destinoRepetirLocalizadorWts) && !strech.origemRepetirLocalizadorWts) {
            totalInternacionalPassagem += strech.origemValorLocalizadorWts;
          }

          // Verifica se o valor destino não foi repetido anteriormente
          if (strech.destinoValorLocalizadorWts && !strech.destinoRepetirLocalizadorWts) {
            totalInternacionalPassagem += strech.destinoValorLocalizadorWts;
          }

          // Verifica se o valor de origem da passagem não foi repetido anteriormente
          if (strech.origemValorPassagem && (!res[i - 1]?.origemRepetirValorPassagem || !res[i - 1]?.destinoRepetirValorPassagem) && !strech.origemRepetirValorPassagem) {
            totalInternacionalPassagem += typeof strech.origemValorPassagem == 'string' ? Number(strech.origemValorPassagem.replace(',', '.')) : strech.origemValorPassagem;
          }

          // Verifica se o valor de destino da passagem não foi repetido anteriormente
          if (strech.destinoValorPassagem && !strech.destinoRepetirValorPassagem) {
            totalInternacionalPassagem += typeof strech.destinoValorPassagem == 'string' ? Number(strech.destinoValorPassagem.replace(',', '.')) : strech.destinoValorPassagem;
          }
        }

      }
    }

    if (rids) {
      for (let rid of rids.getRawValue()) {
        if (rid.trecho?.totalTrecho) {
          if (typeof rid.trecho.totalTrecho == 'string') {
            totalAdvance += Number((rid.trecho.totalTrecho as string).replace(',', '.'));
            if (!telaDetalhes) {
              let totalTrecho = this.calculaRidSemAviao(rid);
              total += Number((totalTrecho as string).replace(',', '.'));
            }
          }

          if (typeof rid.trecho.totalTrecho == 'number') {
            totalAdvance += rid.trecho.totalTrecho;
            if (!telaDetalhes) {
              let totalTrecho = this.calculaRidSemAviao(rid);
              total += totalTrecho;
            }
          }
        }

        for (let expense of rid.detalhesDespesa) {
          if (expense.totalDespesa) {
            if (typeof expense.totalDespesa == 'string') {
              totalAPagar += Number(expense.totalDespesa.replace(',', '.'));
              totalRid += Number(expense.totalDespesa.replace(',', '.'));
            }

            if (typeof expense.totalDespesa == 'number') {
              totalAPagar += expense.totalDespesa;
              totalRid += expense.totalDespesa;
            }
          }
        }
      }
    }

    // Considera RIDs do fluxo 'RID Fornecedor'
    if (ridsFornecedor) {
      for (let rid of ridsFornecedor.getRawValue()) {
        if (rid.trecho?.totalTrecho) {
          if (typeof rid.trecho.totalTrecho == 'string') {
            totalAdvance += Number((rid.trecho.totalTrecho as string).replace(',', '.'));
            if (!telaDetalhes) {
              let totalTrecho = this.calculaRidSemAviao(rid);
              total += Number((totalTrecho as string).replace(',', '.'));
            }
          }

          if (typeof rid.trecho.totalTrecho == 'number') {
            totalAdvance += rid.trecho.totalTrecho;
            if (!telaDetalhes) {
              let totalTrecho = this.calculaRidSemAviao(rid);
              total += totalTrecho;
            }
          }
        }

        for (let expense of rid.detalhesDespesa) {
          if (expense.totalDespesa) {
            if (typeof expense.totalDespesa == 'string') {
              totalAPagar += Number(expense.totalDespesa.replace(',', '.'));
              totalRid += Number(expense.totalDespesa.replace(',', '.'));
            }

            if (typeof expense.totalDespesa == 'number') {
              totalAPagar += expense.totalDespesa;
              totalRid += expense.totalDespesa;
            }
          }
        }
      }
    }

    if (phoneBills?.getRawValue()?.total) {
      total = phoneBills.getRawValue().total;
    }
    if (!telaDetalhes || (telaDetalhes && formValue.fluxo == 4 || telaDetalhes && formValue.fluxo == 7))
      total += totalAPagar;

    if (isPrestacaoContaIntegral) {
      totalBalance = parseFloat(((totalAPagar - totalAdiantado)).toFixed(2));

      if (totalBalance >= 0) { 
        totalAPagar = totalBalance;
        totalAReceber = 0;
      } else {  
        totalAReceber = Math.abs(totalBalance); 
        totalAPagar = 0; 
      }
    } else {
        totalBalance = totalAPagar - totalAReceber;
    }

    const fluxo = this.form.get('fluxo').value;
    const isInternacional = fluxo == 2; // Fluxo Internacional
    
    /**
     * REGRA DE COTAÇÃO:
     * - RID: sempre em Real (não aplica cotação)
     * - Diárias aplicam cotação
     * - Outros valores: seguem o padrão do fluxo
     */
    // totalRid *= 1; (não precisa multiplicar)
    
    if (isInternacional) {
      // Para fluxos internacionais, outros valores aplicam cotação
      totalAdvance *= price;
      totalRealized *= price;
      totalAReceber *= price;
      totalTrechoReal *= price;
      
      // Para valores que podem conter RID: separar RID (Real) de outros valores (moeda estrangeira)
      if (totalRid > 0) {
        // totalAPagar já inclui o valor do RID, então removemos o RID para aplicar cotação apenas no restante
        const totalAPagarSemRid = totalAPagar - totalRid; 
        totalAPagar = totalRid + (totalAPagarSemRid * price); // RID (Real) + outros convertidos
        
        // total já inclui o valor do RID
        const totalSemRid = total - totalRid; 
        total = totalRid + (totalSemRid * price); // RID (Real) + outros convertidos
        
        // totalBalance já inclui o valor do RID
        const totalBalanceSemRid = totalBalance - totalRid; 
        totalBalance = totalRid + (totalBalanceSemRid * price); // RID (Real) + outros convertidos

        const totalTrechoSemRid = totalTrechoReal - totalRid;
        totalTrechoReal = totalRid + (totalTrechoSemRid * price);
      } else {
        // Sem RID: aplica cotação normal
        total *= price;
        totalAPagar *= price;
        totalBalance *= price;
        totalTrechoReal *= price;
      }
      
    } else {
      // Fluxos nacionais: aplica cotação normal
      totalAdvance *= price;
      totalRealized *= price;
      total *= price;
      totalAPagar *= price;
      totalAReceber *= price;
      totalBalance *= price;
      totalTrechoReal *= price;
    }

    if (totalRid != formValue.totalRid)
      this.form.patchValue({ totalRid: totalRid }, { emitEvent: false });

    if (totalAdvance != formValue.totalDiarias)
      this.form.patchValue({ totalDiarias: totalAdvance }, { emitEvent: false });

    if (totalRealized != formValue.totalRealizado)
      this.form.patchValue({ totalRealizado: totalRealized }, { emitEvent: false });

    if (total != formValue.totalSolicitacao)
      this.form.patchValue({ totalSolicitacao: total }, { emitEvent: false });

    if (totalAPagar != formValue.valorAPagar)
      this.form.patchValue({ valorAPagar: totalAPagar }, { emitEvent: false });

    if (totalAReceber != formValue.valorAReceber)
      this.form.patchValue({ valorAReceber: totalAReceber }, { emitEvent: false });

    if (totalBalance != formValue.totalPrestacaoContas)
      this.form.patchValue({ totalPrestacaoContas: totalBalance }, { emitEvent: false });

    if (totalInternacionalDiaria != formValue.totalInternacionalDiaria)
      this.form.patchValue({ totalInternacionalDiaria: totalInternacionalDiaria }, { emitEvent: false });

    if (totalInternacionalPassagem != formValue.totalInternacionalPassagem)
      this.form.patchValue({ totalInternacionalPassagem: totalInternacionalPassagem }, { emitEvent: false });

    if(totalTrechoReal != formValue.totalTrechoReal)
      this.form.patchValue({ totalTrechoReal: totalTrechoReal }, { emitEvent: false });
  }

  calculaRidSemAviao(rid) {
    let totalTrecho = rid.trecho.totalTrecho;

    if(rid.trecho.origemTipoDeLocomocao == 'AVIAO') {
      let valorLocalizadorWts = rid.trecho.origemValorLocalizadorWts != null ? rid.trecho.origemValorLocalizadorWts : 0;   
      let valorOrigemWts = rid.trecho.origemValorPassagem != null ? rid.trecho.origemValorPassagem : 0;   
      if (rid.trecho.origemRepetirLocalizadorWts || rid.trecho.origemRepetirValorPassagem) {
        totalTrecho = 0;
      } else {
        totalTrecho = totalTrecho - (valorLocalizadorWts - valorOrigemWts); 
      }
    }

    if(rid.trecho.destinoTipoDeLocomocao == 'AVIAO') {
      let valorLocalizadorWts = rid.trecho.destinoValorLocalizadorWts ? rid.trecho.destinoValorLocalizadorWts : 0;   
      let valorDestinoWts = rid.trecho.destinoValorPassagem != null ? rid.trecho.destinoValorPassagem : 0;   
      if (rid.trecho.destinoRepetirLocalizadorWts|| rid.trecho.destinoRepetirValorPassagem) {
        totalTrecho = 0;
      } else {
        totalTrecho = totalTrecho - (valorLocalizadorWts - valorDestinoWts); 
      }    
    }

    return totalTrecho;
  }

  exportExcel(filter) {
    return this.http.post(this.urlApi + this.serviceName + '/gerar-excel', filter);
  }

  isNational(fluxo: number, fluxos: IFluxo[]) {
    const fluxoFiltered = fluxos.find(f => f.id == fluxo);

    if (!fluxoFiltered)
      return false;

    return fluxoFiltered.nomeFluxo == 'Nacional';
  }

  isInternational(fluxo: number, fluxos: IFluxo[]) {
    const fluxoFiltered = fluxos.find(f => f.id == fluxo);

    if (!fluxoFiltered)
      return false;

    return fluxoFiltered.nomeFluxo == 'Internacional';
  }

  isParaguaiArgentina(fluxo: number, fluxos: IFluxo[]) {
    const fluxoFiltered = fluxos.find(f => f.id == fluxo);

    if (!fluxoFiltered)
      return false;

    return this.isFluxoParaguaiArgentina(fluxoFiltered.nomeFluxo);
  }

  isRid(fluxos: IFluxo[], fluxo?) {
    const fluxoId: number = fluxo ? fluxo : this.form.getRawValue().fluxo;
    const fluxoFiltered = fluxos.find(f => f.id === fluxoId);

    if (!fluxoFiltered)
      return false;

    return fluxoFiltered.nomeFluxo == 'RID';
  }

  isRidFornecedor(fluxos: IFluxo[], fluxo?) {
    const fluxoId: number = fluxo ? fluxo : this.form.getRawValue().fluxo;
    const fluxoFiltered = fluxos.find(f => f.id === fluxoId);

    if (!fluxoFiltered)
      return false;

    return fluxoFiltered.nomeFluxo == 'RID Fornecedor';
  }

  isPhoneBills(fluxos: IFluxo[], fluxo?) {
    const fluxoId: number = fluxo ? fluxo : this.form.getRawValue().fluxo;
    const fluxoFiltered = fluxos.find(f => f.id === fluxoId);

    if (!fluxoFiltered)
      return false;

    return fluxoFiltered.nomeFluxo == 'Contas Telefônicas';
  }

  checkIsValidType(file: File) {
    const isPDF = file.type === 'application/pdf';
    const isPNG = file.type === 'image/png';
    const isJPG = file.type === 'image/jpg';
    const isJPEG = file.type === 'image/jpeg';

    return isPDF || isPNG || isJPG || isJPEG;
  }

  isValidDates(formControlName: AbstractControl) {
    const startDate = formControlName.get('origemData').value;
    const endDate = formControlName.get('destinoData').value;

    if (typeof startDate == 'object' && typeof endDate == 'object' && startDate && endDate) {
      if (endDate.toDate().valueOf() < startDate.toDate().valueOf())
        return { dateLessThanOrigin: true };

      return null;
    }

    return { invalidDate: true };
  }

  isStrechValidDates(formControlName: AbstractControl) {
    const strechs = formControlName.get('trechos') as FormArray;

    if (strechs.controls.length > 1) {
      for (let i = 1; i < strechs.controls.length; i++) {
        const strech: IStrech = strechs.controls[i].value;
        const lastStrech: IStrech = strechs.controls[i - 1].value;
        const originDate: Moment = strech.origemData;
        const destinyDate: Moment = lastStrech.destinoData;

        if (originDate == null || destinyDate == null) {
          return null;
        }

        if (originDate.toDate().valueOf() < destinyDate.toDate().valueOf()) {
          return { dateLessThanLastStrech: true };
        }
      }
    }

    return null;
  }

  isValidDateFormAccountability(formControlName: AbstractControl) {
    if (formControlName.value) {
      const dateString = formControlName.value;

      if (dateString.length != 8)
        return { invalidDate: true };

      const day = parseInt(dateString[0] + dateString[1]);
      const month = parseInt(dateString[2] + dateString[3]);
      const year = parseInt(dateString[4] + dateString[5] + dateString[6] + dateString[7]);
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const currentDay = new Date().getDate();
      const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

      if (month <= 0 || month > 12)
        return { invalidDate: true };

      // Adjust for leap years
      if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0))
        monthLength[1] = 29;

      if (day <= 0 || day > monthLength[month - 1])
        return { invalidDate: true };

      if (year > currentYear)
        return { dateMoreThanCurrent: true }

      if (month > (currentMonth + 1) && year == currentYear)
        return { dateMoreThanCurrent: true }

      if (year == currentYear && month == (currentMonth + 1) && day > currentDay)
        return { dateMoreThanCurrent: true }

      return null;
    }

    return { invalidDate: true };
  }

  isValidDatesAccountability(formControlName: AbstractControl) {
    const startDate = formControlName.get('diaSaidaDataReal').value;
    const endDate = formControlName.get('diaRetornoDataReal').value;

    if (!startDate || !endDate || startDate.length < 7 || endDate.length < 7)
      return null;

    const origin = {
      day: `${startDate[0]}${startDate[1]}`,
      month: `${startDate[2]}${startDate[3]}`,
      year: `${startDate[4]}${startDate[5]}${startDate[6]}${startDate[7]}`
    }
    const destiny = {
      day: `${endDate[0]}${endDate[1]}`,
      month: `${endDate[2]}${endDate[3]}`,
      year: `${endDate[4]}${endDate[5]}${endDate[6]}${endDate[7]}`
    }

    const originyDate = new Date(`${origin.year}-${origin.month}-${origin.day}`);
    const destinyDate = new Date(`${destiny.year}-${destiny.month}-${destiny.day}`);

    if (originyDate && destinyDate) {
      if (destinyDate.valueOf() < originyDate.valueOf())
        return { startDateMoreThanEnd: true };

      if (destinyDate.valueOf() == originyDate.valueOf()) {
        const originHour = formControlName.get('horaSaidaDataReal').value;
        const destinyHour = formControlName.get('horaRetornoDataReal').value;

        if (originHour != null && destinyHour != null && originHour.length == 4 && destinyHour.length == 4 && originHour > destinyHour)
          return { startHourMoreThanEnd: true };
      }

      return null;
    }

    return { invalidDate: true };
  }

  getNumberTitle(number: number, fluxo: IFluxo): string {
    let titulo = 'Viagem - ';

    switch (fluxo.tipo) {
      case 'AJUDA_DE_CUSTO':
        titulo = 'Ajuda de custo - ';
        break;

      case 'RID':
        titulo = 'RID - ';
        break;
      
      case 'RID_FORNECEDOR':
        titulo = 'RID Fornecedor - ';
        break;

      case 'TELEFONIA':
        titulo = 'Telefonia - ';
        break;

      default:
        titulo = 'Viagem - ';
        break;
    }

    titulo = titulo + number;

    return titulo;
  }

  getMoneyPrefix(fluxo: number, fluxos: IFluxo[]) {
    if (!fluxo || !fluxos)
      return 'R$';

    const returnDollar = this.isInternational(fluxo, fluxos) || this.isParaguaiArgentina(fluxo, fluxos);

    return returnDollar ? '$' : 'R$';
  }

  approveAll(model) {
    return this.http.post(this.urlApi + this.serviceName + '/alterar-status/list', model);
  }

  isAgenteComPendenciasDeViagem(codAgente: number) {
    return this.http.get<AgenteBloqueio[]>(this.urlApi + this.serviceName + '/bloqueio/' + codAgente)
  }

  hasPassagemEmitida(codAgente: number, origemData: string, destinoData: string) {
      return this.http.get<boolean>(`${this.urlApi}${this.serviceName}/passagem-emitida/${codAgente}/${origemData}/${destinoData}`);
  }

  getValorReferencia(idSolicitacao: number) {
    return this.http.get<ValorReferenciaDTO>(
      `${this.urlApi}${this.serviceName}/${idSolicitacao}/valor-referencia`
    );
  }

  getHistoricoAprovacoes(idSolicitacao: number) {
    return this.http.get<HistoricoAprovacaoConsultaDTO[]>(
      `${this.urlApi}${this.serviceName}/${idSolicitacao}/historico-aprovacoes`
    );
  }

}
